/**
 * The KPI row of the person record (D580). On a rider record the five numbers an operator asks first — rides completed, cancellation
 * rate, total spend, rating received and last access — come from the analytics summary (`runtime.customerInsights.summary`) and the
 * client sessions. A number the console cannot read (no market in scope, a refused or failed read) shows "—" with a translated
 * hint saying why — never an error banner and never a quiet zero. A provider record (D619) shows his month, net earnings, acceptance, cancellation and rating from the analytics database, then the last access.
 */
import { type ReactElement } from 'react';
import { type AccessSessionsView, type CustomerSummary, type DirectoryUser, type DriverInsights, type DriverInsightsRate } from '@vello/api-client';
import { KpiGrid, StatCard } from '@vello/web-theme';
import { useT } from '../i18n/provider';
import { type CardState } from './person-card';
import { averageByCurrency, sumByCurrency, usePersonFormat } from './person-format';
import { lastAccessOf, PERSON_SESSIONS_DAYS } from './person-reads';

/** Why a number is not there, in words (translated). */
function useUnavailable(): (state: CardState<unknown> | undefined) => string {
  const { t } = useT();
  return (state) => {
    if (state === undefined) return t('prec.kpi.needsMarket');
    if (state.status === 'error') return t(state.error.status === 403 || state.error.code === 'FORBIDDEN' ? 'prec.kpi.refused' : 'prec.kpi.failed');
    return '';
  };
}

export interface PersonKpisProps {
  readonly user: DirectoryUser;
  readonly isRider: boolean;
  readonly summary: CardState<CustomerSummary> | undefined;
  readonly sessions: CardState<AccessSessionsView> | undefined;
  /** D619: a provider's own numbers from the analytics database (`GET /v1/analytics/drivers/:id/insights`); undefined on a rider. */
  readonly insights?: CardState<DriverInsights> | undefined;
}

export function PersonKpis({ user, isRider, summary, sessions, insights }: PersonKpisProps): ReactElement {
  const { t } = useT();
  const fmt = usePersonFormat();
  const unavailable = useUnavailable();

  const lastAccess = ((): { value: string; hint: string; loading: boolean } => {
    const since = t(isRider ? 'prec.kpi.sinceHint' : 'prec.kpi.sinceHint.driver', { date: fmt.day(user.createdAt) });
    if (sessions === undefined || sessions.status === 'loading') return { value: '—', hint: since, loading: sessions?.status === 'loading' };
    if (sessions.status === 'error') return { value: '—', hint: unavailable(sessions), loading: false };
    const last = lastAccessOf(sessions.value);
    return last === undefined ? { value: '—', hint: t('prec.kpi.lastAccess.none', { days: String(PERSON_SESSIONS_DAYS) }), loading: false } : { value: fmt.when(last), hint: since, loading: false };
  })();

  if (!isRider) {
    // D619: the provider's month, earnings, acceptance, cancellation and rating (analytics database), then the last access.
    const ready = insights?.status === 'ready' ? insights.value : undefined;
    const loading = insights?.status === 'loading';
    const why = unavailable(insights);
    const month = ready?.earnings[0]?.periods.find((p) => p.kind === 'MONTH');
    const rate = (r: DriverInsightsRate | undefined): { value: string; hint: string } =>
      r === undefined
        ? { value: '—', hint: why }
        : r.status === 'FEW_DATA' || r.basisPoints === null
          ? { value: '—', hint: t('prec.dkpi.fewData', { count: fmt.count(r.denominator), min: fmt.count(r.minSample) }) }
          : { value: fmt.percent(r.basisPoints / 10_000), hint: t('prec.dkpi.rate.hint', { count: fmt.count(r.numerator), of: fmt.count(r.denominator) }) };
    const acceptance = rate(ready?.rates.acceptance);
    const cancellation = rate(ready?.rates.cancellation);
    const rating = ready?.ratings;
    return (
      <KpiGrid>
        <StatCard
          label={t('prec.dkpi.rides')}
          value={month !== undefined ? fmt.count(month.current.rides) : ready !== undefined ? fmt.count(0) : '—'}
          hint={month !== undefined ? t('prec.dkpi.rides.hint', { previous: fmt.count(month.previous.rides) }) : ready !== undefined ? t('prec.dchart.empty') : why}
          loading={loading}
        />
        <StatCard
          label={t('prec.dkpi.net')}
          value={month !== undefined ? fmt.money(month.current.net) : '—'}
          hint={month !== undefined ? t('prec.dkpi.net.hint', { gross: fmt.money(month.current.gross), commission: fmt.money(month.current.commission) }) : ready !== undefined ? t('prec.dchart.empty') : why}
          loading={loading}
        />
        <StatCard label={t('prec.dkpi.acceptance')} value={acceptance.value} hint={acceptance.hint} loading={loading} />
        <StatCard label={t('prec.kpi.cancelRate')} value={cancellation.value} hint={cancellation.hint} loading={loading} />
        <StatCard
          label={t('prec.kpi.rating')}
          value={rating !== undefined && rating.averageHundredths !== null ? `${fmt.rating(rating.averageHundredths)} ★` : '—'}
          hint={rating === undefined ? why : rating.count > 0 ? t('prec.kpi.rating.hint', { count: fmt.count(rating.count) }) : t('prec.kpi.rating.none')}
          loading={loading}
        />
        <StatCard label={t('prec.kpi.lastAccess')} value={lastAccess.value} hint={lastAccess.hint} loading={lastAccess.loading} />
      </KpiGrid>
    );
  }

  const ready = summary?.status === 'ready' ? summary.value : undefined;
  const loading = summary?.status === 'loading';
  const why = unavailable(summary);

  const requested = ready?.verticals.reduce((n, x) => n + x.requested, 0) ?? 0;
  const completed = ready?.verticals.reduce((n, x) => n + x.completed, 0) ?? 0;
  const cancelled = ready?.verticals.reduce((n, x) => n + x.cancelled, 0) ?? 0;
  const knownByCustomer = ready?.verticals.filter((x) => x.cancelledByCustomer !== null) ?? [];
  const byCustomer = knownByCustomer.reduce((n, x) => n + (x.cancelledByCustomer ?? 0), 0);
  const spend = sumByCurrency(ready?.verticals.map((x) => x.spend) ?? []);
  const average = averageByCurrency(ready?.verticals.map((x) => ({ total: x.spend, count: x.completed })) ?? []);
  const rating = ready?.ratingsReceived;

  return (
    <KpiGrid>
      <StatCard
        label={t('prec.kpi.completed')}
        value={ready !== undefined ? fmt.count(completed) : '—'}
        hint={ready !== undefined ? t('prec.kpi.completed.hint', { requested: fmt.count(requested) }) : why}
        loading={loading}
      />
      <StatCard
        label={t('prec.kpi.cancelRate')}
        value={ready !== undefined && requested > 0 ? fmt.percent(cancelled / requested) : '—'}
        hint={
          ready === undefined
            ? why
            : requested === 0
              ? t('prec.kpi.cancelRate.none')
              : knownByCustomer.length === 0
                ? t('prec.kpi.cancelRate.hintUnknown', { cancelled: fmt.count(cancelled) })
                : t('prec.kpi.cancelRate.hint', { cancelled: fmt.count(cancelled), byCustomer: fmt.count(byCustomer) })
        }
        loading={loading}
      />
      <StatCard
        label={t('prec.kpi.spend')}
        value={ready !== undefined && spend.length > 0 ? spend.map((m) => fmt.money(m)).join(' · ') : '—'}
        hint={ready === undefined ? why : spend.length === 0 ? t('prec.kpi.spend.none') : t('prec.kpi.spend.hint', { average: average.map((m) => fmt.money(m)).join(' · ') })}
        loading={loading}
      />
      <StatCard
        label={t('prec.kpi.rating')}
        value={rating !== undefined && rating.averageHundredths !== null ? `${fmt.rating(rating.averageHundredths)} ★` : '—'}
        hint={ready === undefined ? why : rating !== undefined && rating.count > 0 ? t('prec.kpi.rating.hint', { count: fmt.count(rating.count) }) : t('prec.kpi.rating.none')}
        loading={loading}
      />
      <StatCard label={t('prec.kpi.lastAccess')} value={lastAccess.value} hint={lastAccess.hint} loading={lastAccess.loading} />
    </KpiGrid>
  );
}
